import { useState, useEffect, useCallback } from 'react';
import { useSuperAdminTheme } from '../../../contexts/SuperAdminThemeContext';
import { cn } from '../../../lib/utils';
import {
  Phone, Mic, Mail, CreditCard, Search, Brain, RefreshCw,
  AlertTriangle, CheckCircle2, XCircle, Loader2,
  Clock, ArrowUpRight, Shield, Zap, Settings, Bell,
  BellRing, History, X, Save, ToggleLeft, ToggleRight
} from 'lucide-react';
import api from '../../../lib/api';
import axios from 'axios';
import { PageHeader, MetricRow } from '../design';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const ICON_MAP = {
  phone: Phone, mic: Mic, mail: Mail, 'credit-card': CreditCard,
  search: Search, brain: Brain,
};

const STATUS_COLORS = {
  active: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  trial: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  restricted: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  suspended: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
  error: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
  unknown: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/20' },
};

function getHeaders() {
  const token = localStorage.getItem('superadmin_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export default function ServiceSubscriptions() {
  const { isDark } = useSuperAdminTheme();
  const [services, setServices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [totalConfigured, setTotalConfigured] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [activeTab, setActiveTab] = useState('services'); // services | thresholds | history

  const fetchServices = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/orchestrator/governance/service-subscriptions`, {
        headers: getHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setServices(data.services || []);
        setAlerts(data.alerts || []);
        setFetchedAt(data.fetched_at);
        setTotalConfigured(data.total_configured || 0);
      }
    } catch (err) {
      console.error('[ServiceSubs] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchServices(true), 60000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchServices]);

  const configured = services.filter(s => s.configured);
  const unconfigured = services.filter(s => !s.configured);
  const alertMap = {};
  alerts.forEach(a => { alertMap[a.service] = a; });

  const tabs = [
    { id: 'services', label: 'Services', icon: Zap },
    { id: 'thresholds', label: 'Alert Thresholds', icon: Settings },
    { id: 'history', label: 'Alert History', icon: History },
  ];

  return (
    <div className="space-y-8" data-testid="service-subscriptions-page">
      <PageHeader
        title="Service Subscriptions"
        subtitle="Monitor subscription status, usage, and billing for all connected services"
        actions={
          <div className="flex items-center gap-3">
            <label className={cn('flex items-center gap-2 text-xs cursor-pointer', isDark ? 'text-slate-400' : 'text-slate-500')}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={e => setAutoRefresh(e.target.checked)}
                className="rounded border-slate-600"
                data-testid="auto-refresh-toggle"
              />
              Auto-refresh
            </label>
            <button
              onClick={() => fetchServices()}
              disabled={loading}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                isDark
                  ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20'
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
              )}
              data-testid="refresh-btn"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Refresh
            </button>
          </div>
        }
      />

      {/* Active Alerts Banner */}
      {alerts.length > 0 && (
        <div className={cn(
          'rounded-xl border p-4',
          alerts.some(a => a.severity === 'critical')
            ? isDark ? 'bg-red-500/5 border-red-500/20' : 'bg-red-50 border-red-200'
            : isDark ? 'bg-amber-500/5 border-amber-500/20' : 'bg-amber-50 border-amber-200'
        )} data-testid="alerts-banner">
          <div className="flex items-center gap-2 mb-2">
            <BellRing className={cn('h-4 w-4', alerts.some(a => a.severity === 'critical') ? 'text-red-400' : 'text-amber-400')} />
            <span className={cn('text-sm font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
              {alerts.length} Active Alert{alerts.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-1.5">
            {alerts.map((a, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={cn(
                  'px-1.5 py-0.5 rounded text-[10px] font-bold uppercase',
                  a.severity === 'critical'
                    ? 'bg-red-500/20 text-red-400'
                    : 'bg-amber-500/20 text-amber-400'
                )}>
                  {a.severity}
                </span>
                <span className={cn('text-xs', isDark ? 'text-slate-300' : 'text-slate-700')}>
                  {a.message}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Bar */}
      <MetricRow cols={4} metrics={[
        { key: 'total-services', label: 'Total Services', value: services.length, icon: Zap },
        { key: 'configured', label: 'Configured', value: totalConfigured, icon: CheckCircle2, tone: 'success' },
        { key: 'active-alerts', label: 'Active Alerts', value: alerts.length, icon: BellRing, tone: alerts.length > 0 ? 'danger' : 'muted' },
        { key: 'last-checked', label: 'Last Checked', value: fetchedAt ? new Date(fetchedAt).toLocaleTimeString() : '--', icon: Clock, tone: 'muted' },
      ]} />

      {/* Tabs */}
      <div className={cn('flex gap-1 p-1 rounded-lg', isDark ? 'bg-slate-800/60' : 'bg-slate-100')}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
              activeTab === tab.id
                ? isDark ? 'bg-slate-700 text-amber-400 shadow-sm' : 'bg-white text-amber-600 shadow-sm'
                : isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'
            )}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
            {tab.id === 'services' && alerts.length > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{alerts.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'services' && (
        loading && services.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className={cn('h-8 w-8 animate-spin', isDark ? 'text-amber-400' : 'text-amber-500')} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="service-cards">
            {configured.map(svc => (
              <ServiceCard key={svc.service} svc={svc} isDark={isDark} alert={alertMap[svc.service]} />
            ))}
            {unconfigured.map(svc => (
              <ServiceCard key={svc.service} svc={svc} isDark={isDark} />
            ))}
          </div>
        )
      )}

      {activeTab === 'thresholds' && <ThresholdsPanel isDark={isDark} />}
      {activeTab === 'history' && <AlertHistoryPanel isDark={isDark} />}
    </div>
  );
}

/* ─── Service Card ─── */
function ServiceCard({ svc, isDark, alert }) {
  const IconComp = ICON_MAP[svc.icon] || Shield;
  const st = STATUS_COLORS[svc.status] || STATUS_COLORS.unknown;
  const isConfigured = svc.configured;

  return (
    <div
      className={cn(
        'rounded-xl border p-5 transition-all hover:shadow-lg relative',
        alert
          ? alert.severity === 'critical'
            ? isDark ? 'bg-red-500/5 border-red-500/30 hover:border-red-500/50' : 'bg-red-50/50 border-red-300 hover:border-red-400'
            : isDark ? 'bg-amber-500/5 border-amber-500/30 hover:border-amber-500/50' : 'bg-amber-50/50 border-amber-300 hover:border-amber-400'
          : isDark
            ? isConfigured ? 'bg-slate-800/60 border-slate-700/60 hover:border-slate-600' : 'bg-slate-800/30 border-slate-700/30 opacity-60'
            : isConfigured ? 'bg-white border-slate-200 hover:border-slate-300' : 'bg-slate-50 border-slate-200 opacity-60'
      )}
      data-testid={`service-card-${svc.service?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    >
      {/* Alert Badge */}
      {alert && (
        <div className={cn(
          'absolute -top-2 -right-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border shadow-sm',
          alert.severity === 'critical'
            ? 'bg-red-500 text-white border-red-600'
            : 'bg-amber-500 text-white border-amber-600'
        )} data-testid={`alert-badge-${svc.service?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
          <AlertTriangle className="h-2.5 w-2.5" />
          {alert.severity.toUpperCase()}
        </div>
      )}

      {/* Top Row */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn('p-2.5 rounded-lg', isDark ? 'bg-slate-700/80' : 'bg-slate-100')}>
            <IconComp className={cn('h-5 w-5', isConfigured ? 'text-amber-400' : isDark ? 'text-slate-500' : 'text-slate-400')} />
          </div>
          <div>
            <h3 className={cn('font-semibold text-sm', isDark ? 'text-white' : 'text-slate-900')}>{svc.service}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              {isConfigured ? (
                <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium', st.bg, st.text, 'border', st.border)}>
                  <CheckCircle2 className="h-2.5 w-2.5" />
                  {(svc.status || 'active').toUpperCase()}
                </span>
              ) : (
                <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium',
                  'bg-slate-500/10 text-slate-400 border border-slate-500/20')}>
                  <XCircle className="h-2.5 w-2.5" />NOT CONFIGURED
                </span>
              )}
              {svc.plan && (
                <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full',
                  isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600')}>
                  {svc.plan}
                </span>
              )}
            </div>
          </div>
        </div>
        {svc.upgrade_url && isConfigured && (
          <a href={svc.upgrade_url} target="_blank" rel="noopener noreferrer"
            className={cn(
              'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              isDark ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20'
                : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
            )}
            data-testid={`upgrade-btn-${svc.service?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
          >
            <ArrowUpRight className="h-3 w-3" />Upgrade Plan
          </a>
        )}
      </div>

      {/* Alert Message */}
      {alert && (
        <div className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg mb-3 text-xs',
          alert.severity === 'critical'
            ? isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-100 text-red-700'
            : isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-100 text-amber-700'
        )} data-testid={`alert-msg-${svc.service?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{alert.message}</span>
        </div>
      )}

      {!isConfigured ? (
        <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
          API key not configured. Add credentials in your environment to enable monitoring.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {svc.service === 'Twilio' && (
              <>
                <MetricCell isDark={isDark} label="Balance" value={svc.balance || '--'} highlight={alert?.severity === 'critical'} />
                <MetricCell isDark={isDark} label="Calls This Month" value={svc.usage_calls ?? '--'} />
                <MetricCell isDark={isDark} label="Cost This Month" value={svc.usage_cost || '--'} />
              </>
            )}
            {svc.service === 'ElevenLabs' && (
              <>
                <MetricCell isDark={isDark} label="Characters Used" value={`${(svc.character_count || 0).toLocaleString()} / ${(svc.character_limit || 0).toLocaleString()}`} />
                <MetricCell isDark={isDark} label="Remaining" value={(svc.character_remaining || 0).toLocaleString()} highlight={svc.character_remaining < 1000} />
                <MetricCell isDark={isDark} label="Resets In" value={svc.days_until_reset != null ? `${svc.days_until_reset} days` : '--'} />
              </>
            )}
            {svc.service === 'SendGrid' && (
              <>
                <MetricCell isDark={isDark} label="Emails Sent" value={svc.emails_sent_this_month ?? '--'} />
                <MetricCell isDark={isDark} label="Delivered" value={svc.emails_delivered ?? '--'} />
                <MetricCell isDark={isDark} label="Delivery Rate" value={svc.delivery_rate ? `${svc.delivery_rate}%` : '--'} highlight={alert?.severity} />
              </>
            )}
            {svc.service === 'Stripe' && (
              <>
                <MetricCell isDark={isDark} label="Balance" value={svc.balance || '--'} highlight={alert?.severity === 'critical'} />
                <MetricCell isDark={isDark} label="Mode" value={svc.mode?.toUpperCase() || '--'} />
                <MetricCell isDark={isDark} label="Payouts" value={svc.payouts_enabled ? 'Enabled' : 'Disabled'} />
              </>
            )}
            {svc.service === 'Apollo.io' && (
              <>
                <MetricCell isDark={isDark} label="Status" value={(svc.status || '--').toUpperCase()} />
                <MetricCell isDark={isDark} label="Plan" value={svc.plan || '--'} />
                <MetricCell isDark={isDark} label="API" value="Connected" />
              </>
            )}
            {svc.service?.includes('Emergent') && (
              <>
                <MetricCell isDark={isDark} label="Models" value="GPT / Claude / Gemini" />
                <MetricCell isDark={isDark} label="Status" value="Active" />
                <MetricCell isDark={isDark} label="Billing" value="Usage-based" />
              </>
            )}
          </div>
          {svc.service === 'ElevenLabs' && svc.usage_pct != null && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className={cn('text-[10px]', isDark ? 'text-slate-400' : 'text-slate-500')}>Character Usage</span>
                <span className={cn('text-[10px] font-medium', svc.usage_pct > 80 ? 'text-red-400' : isDark ? 'text-slate-300' : 'text-slate-600')}>
                  {svc.usage_pct}%
                </span>
              </div>
              <div className={cn('h-1.5 rounded-full overflow-hidden', isDark ? 'bg-slate-700' : 'bg-slate-200')}>
                <div
                  className={cn('h-full rounded-full transition-all', svc.usage_pct > 80 ? 'bg-red-500' : svc.usage_pct > 50 ? 'bg-amber-500' : 'bg-emerald-500')}
                  style={{ width: `${Math.min(svc.usage_pct, 100)}%` }}
                />
              </div>
            </div>
          )}
          <div className={cn('flex flex-wrap gap-x-4 gap-y-1 pt-2 border-t text-[10px]',
            isDark ? 'border-slate-700/60 text-slate-500' : 'border-slate-100 text-slate-400')}>
            {svc.account_sid && <span>SID: {svc.account_sid}</span>}
            {svc.account_id && <span>ID: {svc.account_id}</span>}
            {svc.email && <span>{svc.email}</span>}
            {svc.phone_number && <span>{svc.phone_number}</span>}
            {svc.billing_period && <span>Billing: {svc.billing_period}</span>}
            {svc.api_key && <span>Key: {svc.api_key}</span>}
            {svc.note && <span>{svc.note}</span>}
            {svc.error && <span className="text-red-400">Error: {svc.error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Metric Cell ─── */
function MetricCell({ isDark, label, value, highlight }) {
  return (
    <div className={cn('p-2 rounded-lg', isDark ? 'bg-slate-700/40' : 'bg-slate-50')}>
      <p className={cn('text-[10px] mb-0.5', isDark ? 'text-slate-500' : 'text-slate-400')}>{label}</p>
      <p className={cn('text-sm font-semibold truncate', highlight ? 'text-red-400' : isDark ? 'text-white' : 'text-slate-900')}>{value}</p>
    </div>
  );
}

/* ─── Thresholds Panel ─── */
function ThresholdsPanel({ isDark }) {
  const [thresholds, setThresholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/orchestrator/governance/service-alerts/thresholds`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          setThresholds(data.thresholds || []);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const updateThreshold = async (service, updates) => {
    setSaving(service);
    const current = thresholds.find(t => t.service === service);
    const body = { ...current, ...updates };
    try {
      const res = await fetch(`${API_BASE}/api/orchestrator/governance/service-alerts/thresholds/${encodeURIComponent(service)}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setThresholds(prev => prev.map(t => t.service === service ? { ...t, ...updates } : t));
      }
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className={cn('h-8 w-8 animate-spin', isDark ? 'text-amber-400' : 'text-amber-500')} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="thresholds-panel">
      <div className={cn('rounded-xl border p-4', isDark ? 'bg-slate-800/40 border-slate-700/50' : 'bg-white border-slate-200')}>
        <div className="flex items-center gap-2 mb-4">
          <Bell className={cn('h-4 w-4', isDark ? 'text-amber-400' : 'text-amber-500')} />
          <h3 className={cn('font-semibold text-sm', isDark ? 'text-white' : 'text-slate-900')}>
            Alert Threshold Configuration
          </h3>
        </div>
        <p className={cn('text-xs mb-4', isDark ? 'text-slate-400' : 'text-slate-500')}>
          Configure when to trigger alerts and email notifications for each service. Alerts fire when a metric crosses the threshold.
        </p>

        <div className="space-y-3">
          {thresholds.map(th => (
            <ThresholdRow key={th.service} th={th} isDark={isDark} saving={saving} onUpdate={updateThreshold} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Threshold Row ─── */
function ThresholdRow({ th, isDark, saving, onUpdate }) {
  const [value, setValue] = useState(th.value);
  const isSaving = saving === th.service;

  return (
    <div className={cn(
      'flex items-center gap-4 p-3 rounded-lg border',
      th.enabled
        ? isDark ? 'bg-slate-700/30 border-slate-600/40' : 'bg-slate-50 border-slate-200'
        : isDark ? 'bg-slate-800/30 border-slate-700/20 opacity-50' : 'bg-slate-100/50 border-slate-200/50 opacity-50'
    )} data-testid={`threshold-row-${th.service?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
      {/* Service Name */}
      <div className="w-32 flex-shrink-0">
        <p className={cn('text-sm font-medium', isDark ? 'text-white' : 'text-slate-900')}>{th.service}</p>
        <p className={cn('text-[10px]', isDark ? 'text-slate-500' : 'text-slate-400')}>{th.label}</p>
      </div>

      {/* Operator + Value */}
      <div className="flex items-center gap-2 flex-1">
        <span className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
          {th.operator === 'below' ? 'Alert when below' : 'Alert when above'}
        </span>
        <div className="flex items-center gap-1">
          <span className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>{th.unit}</span>
          <input
            type="number"
            value={value}
            onChange={e => setValue(e.target.value)}
            className={cn(
              'w-20 px-2 py-1 rounded text-sm text-center border',
              isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'
            )}
            data-testid={`threshold-value-${th.service?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
          />
        </div>
        <button
          onClick={() => onUpdate(th.service, { value: parseFloat(value) })}
          disabled={isSaving || parseFloat(value) === th.value}
          className={cn(
            'p-1.5 rounded-lg transition-colors',
            parseFloat(value) !== th.value
              ? isDark ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' : 'bg-amber-100 text-amber-600 hover:bg-amber-200'
              : isDark ? 'text-slate-600' : 'text-slate-300'
          )}
          data-testid={`save-threshold-${th.service?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
        >
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Enabled Toggle */}
      <button
        onClick={() => onUpdate(th.service, { enabled: !th.enabled })}
        className={cn('p-1 rounded transition-colors', th.enabled ? 'text-emerald-400' : isDark ? 'text-slate-500' : 'text-slate-400')}
        title={th.enabled ? 'Disable alert' : 'Enable alert'}
        data-testid={`toggle-enabled-${th.service?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
      >
        {th.enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
      </button>

      {/* Email Toggle */}
      <button
        onClick={() => onUpdate(th.service, { email_notify: !th.email_notify })}
        className={cn('p-1 rounded transition-colors', th.email_notify ? 'text-blue-400' : isDark ? 'text-slate-500' : 'text-slate-400')}
        title={th.email_notify ? 'Disable email' : 'Enable email'}
        data-testid={`toggle-email-${th.service?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
      >
        <Mail className="h-4 w-4" />
      </button>
    </div>
  );
}

/* ─── Alert History Panel ─── */
function AlertHistoryPanel({ isDark }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/orchestrator/governance/service-alerts/history`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          setHistory(data.history || []);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className={cn('h-8 w-8 animate-spin', isDark ? 'text-amber-400' : 'text-amber-500')} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="alert-history-panel">
      <div className={cn('rounded-xl border overflow-hidden', isDark ? 'bg-slate-800/40 border-slate-700/50' : 'bg-white border-slate-200')}>
        <div className={cn('flex items-center gap-2 p-4 border-b', isDark ? 'border-slate-700/50' : 'border-slate-200')}>
          <History className={cn('h-4 w-4', isDark ? 'text-amber-400' : 'text-amber-500')} />
          <h3 className={cn('font-semibold text-sm', isDark ? 'text-white' : 'text-slate-900')}>
            Alert History ({history.length})
          </h3>
        </div>

        {history.length === 0 ? (
          <div className={cn('p-8 text-center text-sm', isDark ? 'text-slate-500' : 'text-slate-400')}>
            No alerts have been triggered yet.
          </div>
        ) : (
          <div className="divide-y divide-slate-700/30">
            {history.map((h, i) => (
              <div key={i} className={cn('flex items-center gap-3 px-4 py-3', isDark ? 'hover:bg-slate-700/20' : 'hover:bg-slate-50')}
                data-testid={`history-row-${i}`}
              >
                <div className={cn(
                  'flex-shrink-0 w-2 h-2 rounded-full',
                  h.resolved ? 'bg-emerald-400' : h.severity === 'critical' ? 'bg-red-400' : 'bg-amber-400'
                )} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      'px-1.5 py-0.5 rounded text-[10px] font-bold uppercase',
                      h.severity === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                    )}>
                      {h.severity}
                    </span>
                    <span className={cn('text-xs font-medium', isDark ? 'text-white' : 'text-slate-900')}>{h.service}</span>
                    {h.resolved && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/20 text-emerald-400">RESOLVED</span>
                    )}
                    {h.email_sent && (
                      <Mail className="h-3 w-3 text-blue-400" title="Email sent" />
                    )}
                  </div>
                  <p className={cn('text-xs mt-0.5 truncate', isDark ? 'text-slate-400' : 'text-slate-500')}>{h.message}</p>
                </div>
                <div className={cn('text-[10px] flex-shrink-0', isDark ? 'text-slate-500' : 'text-slate-400')}>
                  {h.triggered_at ? new Date(h.triggered_at).toLocaleString() : '--'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
